import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { useAuthSession } from "../../contexts/AuthSessionContext";
import {
  hasSuperAdminRole,
  canSeeLibraryNavigation,
  canAccessContractsLibraryAdmin,
  canAccessRfpLibraryAdmin,
  canAccessPrizeRulesLibraryAdmin,
} from "../../utils/sessionModuleAccess";

type PermissionRouteProps = {
  readonly children: ReactElement;
  readonly requireSuperAdmin?: boolean;
  readonly requireAnyAdminRole?: boolean;
  /** When true, user must have PrizeLogic / game rule capability (Library tile parity). */
  readonly requirePrizeRulesAccess?: boolean;
  /** `/library` — contract, RFP, or prize-rules admin (or super). */
  readonly requireLibrarySurface?: boolean;
  readonly requireContractsLibraryAdmin?: boolean;
  readonly requireRfpLibraryAdmin?: boolean;
  readonly requirePrizeRulesLibraryAdmin?: boolean;
};

export const PermissionRoute = ({
  children,
  requireSuperAdmin = false,
  requireAnyAdminRole = false,
  requirePrizeRulesAccess = false,
  requireLibrarySurface = false,
  requireContractsLibraryAdmin = false,
  requireRfpLibraryAdmin = false,
  requirePrizeRulesLibraryAdmin = false,
}: PermissionRouteProps) => {
  const { adminRoles, isShellAuthenticated, effectiveAccess } = useAuthSession();

  if (!isShellAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // SUPER_ADMIN overrides page-level permission gates.
  if (hasSuperAdminRole(adminRoles)) {
    return children;
  }

  if (requireSuperAdmin && !adminRoles.includes("SUPER_ADMIN")) {
    return <Navigate to="/my-contracts" replace />;
  }

  if (requireAnyAdminRole && adminRoles.length === 0) {
    return <Navigate to="/my-contracts" replace />;
  }

  if (requireLibrarySurface && !canSeeLibraryNavigation(adminRoles)) {
    return <Navigate to="/my-contracts" replace />;
  }

  if (requireContractsLibraryAdmin && !canAccessContractsLibraryAdmin(adminRoles)) {
    return <Navigate to="/my-contracts" replace />;
  }

  if (requireRfpLibraryAdmin && !canAccessRfpLibraryAdmin(adminRoles)) {
    return <Navigate to="/my-contracts" replace />;
  }

  if (requirePrizeRulesLibraryAdmin && !canAccessPrizeRulesLibraryAdmin(adminRoles)) {
    return <Navigate to="/my-contracts" replace />;
  }

  if (requirePrizeRulesAccess && effectiveAccess?.can_access_prize_rules !== true) {
    return <Navigate to="/library" replace />;
  }

  return children;
};
